/**
 * One page request against any event-log route, as the wire carries it: the interface every event log shares.
 *
 * With `since`, a delta: rows created after it, oldest first. Without, history: the newest first. `until` bounds
 * either from above; `cursor` continues a page in the same order, and a cursor from the other order is refused. Every
 * instant is an ISO string.
 */
export type EventLogQuery = {
  since?: string;
  until?: string;
  cursor?: string;
  limit?: number;
};

/**
 * The names of the shared parameters, so a spec can refuse a lens or a shape parameter that would shadow one.
 */
export const EVENT_LOG_SHARED_PARAMS = [
  "since",
  "until",
  "cursor",
  "limit",
] as const satisfies readonly (keyof EventLogQuery)[];

export type EventLogSharedParam = (typeof EVENT_LOG_SHARED_PARAMS)[number];

/**
 * The event-log fields a page carries beside its rows.
 */
export type EventLogPageMarks = {
  /** Continues this page in the same order; absent when the page is the last. */
  cursor?: string;
  /** What the client hands back as `since` next time. */
  syncTimestamp: string;
};

/**
 * An event-log route's page size: what a request without a `limit` gets, and the most it serves; more is clamped.
 */
export type EventLogLimits = { defaultLimit: number; maxLimit: number };

/** The page size of the event logs a cold client walks in bulk: feed items, driver transcripts, account events. */
export const BULK_EVENT_LOG_LIMITS: EventLogLimits = { defaultLimit: 1_000, maxLimit: 10_000 };

/**
 * The page size of the event logs a client polls after a ping: acknowledgements, and the TMS event streams.
 *
 * The cap matches the bulk logs': a cold client walks a stream's two days in bulk too, and a poll that asks for less
 * gets less.
 */
export const STREAM_EVENT_LOG_LIMITS: EventLogLimits = { defaultLimit: 100, maxLimit: 10_000 };

/** How many days of history the web loads on boot. */
export const HISTORY_WINDOW_DAYS = 7;

/**
 * How far back a floored event-log request may start, in days.
 *
 * A workspace-wide feed-item or transcript read is a fleet-wide index range from `since` forward, so its cost follows
 * how far back the floor is. Twice the history window leaves room for a slow clock or a client asking for a few days
 * more, while an unbounded floor would let one request scan the whole table.
 */
export const EVENT_LOG_MAX_AGE_DAYS = 2 * HISTORY_WINDOW_DAYS;
